import React, { CSSProperties, FC, ReactNode } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

interface AnimatedButtonProps {
  onPress: () => void;
  disabled?: boolean;
  /** When false, the button doesn't stretch to fill its container's width. */
  fullWidth?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Applied to the outer wrapper rather than the button itself — used to
   * make a button flex to fill remaining space in a row (e.g. `{ flex: 1 }`). */
  wrapperStyle?: CSSProperties;
  ariaLabel?: string;
  children: ReactNode;
}

/**
 * Shared press-scale wrapper for the Suggested Blocks UX (ApproveDismissBar,
 * SuggestionChip, SuggestedBlockActionsRow) — mirrors the RN AnimatedButton's
 * prop surface (onPress/disabled/fullWidth/style/wrapperStyle) so those ports
 * stay close to their RN source.
 */
const AnimatedButton: FC<AnimatedButtonProps> = ({
  onPress,
  disabled = false,
  fullWidth = true,
  className,
  style,
  wrapperStyle,
  ariaLabel,
  children,
}) => (
  <div style={{ ...(fullWidth ? { width: '100%' } : undefined), ...wrapperStyle }}>
    <motion.button
      type="button"
      onClick={onPress}
      disabled={disabled}
      aria-label={ariaLabel}
      whileTap={disabled ? undefined : { scale: 0.96 }}
      transition={{ duration: 0.1 }}
      className={clsx('animated-button', className)}
      style={{
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        width: fullWidth ? '100%' : undefined,
        ...style,
      }}
    >
      {children}
    </motion.button>
  </div>
);

export default AnimatedButton;
